import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DateRange } from '@/components/DataTable/FilterControls/Filters/DateRangeFilter'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { Note } from '@/components/Note'
import { useCustomerPopulationFilters } from '@/hooks/filters/credit/useCustomerPopulationFilters'
import {
  customersSearchSchema,
  exportCustomersCsv,
  useCustomers,
} from '@/hooks/queries/credit/useCustomers'
import { useMassClrPreparerEligibility } from '@/hooks/queries/credit/useMassClr'
import { useReviewDateWarnings } from '@/hooks/queries/credit/useReviewDateWarnings'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import {
  CLEARED_POPULATION_SEARCH,
  hasAnyPopulationFilter,
  pickPopulationSearch,
} from '@/lib/utils/populationFilters'
import type { OverallBlockStatus } from '@repo/constants'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { addMonths, differenceInHours, subDays } from 'date-fns'
import { AlertTriangleIcon, LayersIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomersDataTable } from './-components/CustomersDataTable'

/**
 * How old the denormalized customer data may get before the page calls it stale. The recompute
 * worker rewrites every row nightly, so anything past a day means a run was missed and the list is
 * showing numbers no one should act on without checking SAP.
 */
const STALE_AFTER_HOURS = 24

/** The Overall Block status the review-date banners drill down with. */
const ACTIVE_ONLY: OverallBlockStatus[] = ['active']

export const Route = createFileRoute('/_auth/_modules/credit/customers/')({
  component: RouteComponent,
  validateSearch: (search) => customersSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const { formatDateWithTime, formatDistanceToNowLocalized } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  // The filter panel drafts: same hook as New Mass Review, so the two screens share one vocabulary.
  const population = useCustomerPopulationFilters(search)

  const reviewWarningsQuery = useReviewDateWarnings()
  const reviewWarnings = reviewWarningsQuery.data
  const hasReviewWarnings =
    reviewWarnings !== undefined &&
    (reviewWarnings.pastDueCount > 0 || reviewWarnings.dueSoonCount > 0)

  // The banner counts exclude closed accounts, so the drill-down pins the Overall Block filter to
  // "active" alongside the date range — otherwise the list would show more rows than the banner said.
  // Everything else is cleared first: the banner states an absolute count, so leaving prior filters
  // stacked on top would show fewer rows than it promised.
  const filterByNextReviewDateRange = (range: DateRange) => {
    const pinned = {
      overallBlock: ACTIVE_ONLY,
      nextReviewDateFrom: range.from,
      nextReviewDateTo: range.to,
    }
    setKeyword('')
    population.set(pinned)
    navigate({
      search: { ...search, ...CLEARED_POPULATION_SEARCH, ...pinned, page: 1 },
    })
  }
  const filterPastDueReviews = () =>
    filterByNextReviewDateRange({ from: undefined, to: subDays(new Date(), 1) })
  const filterDueSoonReviews = () =>
    filterByNextReviewDateRange({ from: new Date(), to: addMonths(new Date(), 1) })

  // The committed (URL) filters and sort. The list pages them; the export takes them whole.
  const appliedPopulation = pickPopulationSearch(search)
  const appliedQueries = { ...appliedPopulation, sort: search.sort }

  const customersQuery = useCustomers({ ...appliedQueries, page: search.page ?? 1, pageSize })

  // The quick action into New Mass Review carries the COMMITTED filters, so the population it
  // seeds is exactly the list on screen. Only for someone the DOA lets prepare a mass run: a
  // viewer without the CLR permission gets no eligibility answer at all, which hides it too.
  const canPrepare = useMassClrPreparerEligibility().data?.canPrepare
  const canStartMassReview = hasAnyPopulationFilter(appliedPopulation) && canPrepare === true

  const denormalizedUpdatedAt = customersQuery.data?.denormalizedUpdatedAt
  const dataUpdatedAt = denormalizedUpdatedAt ? new Date(denormalizedUpdatedAt) : null
  const isDataStale =
    dataUpdatedAt !== null && differenceInHours(new Date(), dataUpdatedAt) >= STALE_AFTER_HOURS

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...population.selected,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    population.clear()

    navigate({
      search: { ...search, ...CLEARED_POPULATION_SEARCH, page: 1 },
    })
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <PageHeader>{t('customers.pageHeader')}</PageHeader>
        {dataUpdatedAt && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <HStack gap="xs" className="cursor-default">
                  {isDataStale && (
                    <AlertTriangleIcon
                      className="text-critical-text size-3.5 shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <Text tone={isDataStale ? 'critical' : 'muted'} size="xs">
                    {t('customers.dataUpdated', {
                      date: formatDistanceToNowLocalized(dataUpdatedAt, { addSuffix: true }),
                    })}
                  </Text>
                </HStack>
              </TooltipTrigger>
              <TooltipContent>
                <VStack gap="xs">
                  <span>{formatDateWithTime(dataUpdatedAt)}</span>
                  {isDataStale && (
                    <span>{t('customers.dataStale', { hours: STALE_AFTER_HOURS })}</span>
                  )}
                </VStack>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Spread>

      {(hasReviewWarnings || canStartMassReview) && (
        <VStack gap="sm">
          {reviewWarnings && reviewWarnings.pastDueCount > 0 && (
            <Note
              tone="critical"
              icon={AlertTriangleIcon}
              note={t('customers.reviewWarnings.pastDue.banner', {
                count: reviewWarnings.pastDueCount,
              })}
              action={
                <Button type="button" variant="secondary" size="sm" onClick={filterPastDueReviews}>
                  {t('customers.reviewWarnings.viewCustomers')}
                </Button>
              }
            />
          )}
          {reviewWarnings && reviewWarnings.dueSoonCount > 0 && (
            <Note
              tone="warning"
              icon={AlertTriangleIcon}
              note={t('customers.reviewWarnings.dueSoon.banner', {
                count: reviewWarnings.dueSoonCount,
              })}
              action={
                <Button type="button" variant="secondary" size="sm" onClick={filterDueSoonReviews}>
                  {t('customers.reviewWarnings.viewCustomers')}
                </Button>
              }
            />
          )}
          {canStartMassReview && (
            <Note
              tone="info"
              icon={LayersIcon}
              note={t('customers.massReview.banner')}
              action={
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to="/credit/credit-limit-reviews/mass/new" search={appliedPopulation}>
                    {t('customers.massReview.start')}
                  </Link>
                </Button>
              }
            />
          )}
        </VStack>
      )}

      <Card>
        <CardContent className="pt-6">
          <CustomersDataTable
            filters={population.filters}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('customers.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            customersQuery={customersQuery}
            toolbarActions={
              <ExportCsvButton
                getCsvExport={() => exportCustomersCsv(appliedQueries)}
                rowCount={customersQuery.data?.rowCount ?? 0}
                label={t('customers.export')}
              />
            }
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
